import { BECategory } from 'constants/categories';
import type { RestrictedTradingCategory } from 'constants/featureFlags';
import { useFeatureFlagStore } from 'lib/stores/featureFlagStore';
import { isSportsMilestoneType, type MilestoneType } from 'models/milestones';

import { isGeoComplyUiEnabled } from './geoComplyFeatureGate';
import {
    isGeoComplyVerificationCurrent,
    useGeoComplyStore,
    type GeoComplyDenyReason,
    type GeoComplyStatus,
} from './geoComplyStore';

export const SPORTS_LOCATION_VERIFICATION_MESSAGE = 'You need to verify your location to trade.';
export const SPORTS_LOCATION_PERMISSION_HELP = 'Allow location access in your browser, then try again.';

/**
 * Built-in fallback for the geo-enforced restricted-trading entries,
 * mirroring `restricted_trading_categories` in product-metadata
 * `app_casual/feature_flags.json5`. The feature-flag store's fetched
 * entries override this whenever any geo-flagged entry is present.
 */
const DEFAULT_GEO_RESTRICTED_ENTRIES: readonly RestrictedTradingCategory[] = [
    { state: 'NV', categories: ['Sports', 'Elections', 'Entertainment'], subcategories: ['Sports'], geo: true },
    { state: 'MI', categories: ['Sports'], geo: true },
];

function getGeoRestrictedEntries(): readonly RestrictedTradingCategory[] {
    const fetched = (useFeatureFlagStore.getState().restrictedTradingCategories ?? []).filter(
        (entry) => entry.geo === true,
    );
    return fetched.length > 0 ? fetched : DEFAULT_GEO_RESTRICTED_ENTRIES;
}

/**
 * Lowercased categories and subcategories whose orders require a
 * geolocation compliance check in the given state.
 */
function geoCategoriesForState(entry: RestrictedTradingCategory): Set<string> {
    return new Set(
        [...entry.categories, ...(entry.subcategories ?? [])]
            .map((category) => category.trim().toLowerCase())
            .filter(Boolean),
    );
}

export interface SportsTradingMarketData {
    category?: string | null;
    milestoneType?: MilestoneType;
}

declare const sportsTradingScopeBrand: unique symbol;

export interface SportsTradingScope {
    /** Lowercased category values of the trade's markets (sports milestones report 'sports'). */
    readonly categoryValues: readonly string[];
    /** True when any market lacked classification data; such trades fail closed. */
    readonly containsUnknownMarkets: boolean;
    readonly requiresLocationVerification: boolean;
    readonly [sportsTradingScopeBrand]: true;
}

/**
 * Thrown by the trade guard so callers can tell a client-side location block
 * apart from a server error without matching on message copy.
 */
export class SportsTradingLocationError extends Error {
    constructor(
        message: string,
        readonly reason: 'blocked' | 'verification-required',
    ) {
        super(message);
        this.name = 'SportsTradingLocationError';
    }
}

/**
 * Whether the trade's categories are geo-restricted anywhere per the current
 * configuration. The remote config loads asynchronously, so enforcement
 * points must call this at decision time rather than trusting the snapshot
 * a derived scope captured.
 */
export function scopeRequiresLocationVerification(
    scope: Pick<SportsTradingScope, 'categoryValues' | 'containsUnknownMarkets'>,
): boolean {
    if (scope.containsUnknownMarkets) return true;
    return getGeoRestrictedEntries().some((entry) => {
        const geoCategories = geoCategoriesForState(entry);
        return scope.categoryValues.some((value) => geoCategories.has(value));
    });
}

export function deriveSportsTradingScope(markets: readonly SportsTradingMarketData[]): SportsTradingScope {
    const categoryValues = new Set<string>();
    let containsUnknownMarkets = markets.length === 0;

    for (const market of markets) {
        const category = market.category?.trim().toLowerCase();
        if (category) categoryValues.add(category);
        if (isSportsMilestoneType(market.milestoneType)) {
            categoryValues.add(BECategory.SPORTS.toLowerCase());
        } else if (!category) {
            containsUnknownMarkets = true;
        }
    }

    const partialScope = { categoryValues: [...categoryValues], containsUnknownMarkets };
    const scope: Omit<SportsTradingScope, typeof sportsTradingScopeBrand> = {
        ...partialScope,
        requiresLocationVerification: scopeRequiresLocationVerification(partialScope),
    };
    return scope as SportsTradingScope;
}

/**
 * Whether the confirmed region blocks this trade: true when any of the
 * trade's categories is geo-restricted in that region. A missing scope or
 * one containing unclassified markets fails closed in restricted regions.
 */
function isScopeBlockedInRegion(scope: SportsTradingScope | undefined, usRegionCode: string | null): boolean {
    if (!usRegionCode) return false;
    const entry = getGeoRestrictedEntries().find((candidate) => candidate.state.trim().toUpperCase() === usRegionCode);
    if (!entry) return false;
    if (!scope || scope.containsUnknownMarkets) return true;
    const geoCategories = geoCategoriesForState(entry);
    return scope.categoryValues.some((value) => geoCategories.has(value));
}

export interface GeoComplyDenyMessage {
    title: string;
    description: string;
}

const UNKNOWN_DENY_MESSAGE: GeoComplyDenyMessage = {
    title: 'Trading unavailable in your area',
    description: "To comply with local laws, trading is currently unavailable. You'll still be able to browse markets.",
};

/**
 * Deny copy per reason, mirroring the mobile apps' built-in
 * `geocomply_deny_messages`. Reasons without an entry use the unknown copy.
 */
const DENY_MESSAGES: Partial<Record<GeoComplyDenyReason, GeoComplyDenyMessage>> = {
    failed_with_precise_location: {
        title: 'Trading is currently unavailable',
        description: 'To comply with local laws, geo-spoofing software is not allowed.',
    },
    blocked_software: {
        title: 'Trading is currently unavailable',
        description: 'To comply with local laws, altered or tampered software is not allowed.',
    },
    not_tradeable_state: {
        title: 'Trading limited in your area',
        description: 'To comply with local laws, {categories} trading is currently unavailable.',
    },
    unknown: UNKNOWN_DENY_MESSAGE,
};

/** Spells out categories as a lowercase list (e.g. "sports, elections and entertainment"). */
function spelledOutCategories(categories: readonly string[]): string {
    const names = categories.map((category) => category.toLowerCase());
    if (names.length === 1) return names[0];
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The deny copy for a blocked state. `{categories}` is filled with the
 * confirmed region's configured restricted categories; when that cannot be
 * resolved the unknown copy is used, matching the mobile apps.
 */
export function getGeoComplyDenyMessage(
    denyReason: GeoComplyDenyReason,
    usRegionCode: string | null,
): GeoComplyDenyMessage {
    const entry = DENY_MESSAGES[denyReason] ?? UNKNOWN_DENY_MESSAGE;
    if (!entry.description.includes('{categories}')) return entry;

    const regionEntry = usRegionCode
        ? getGeoRestrictedEntries().find((candidate) => candidate.state.trim().toUpperCase() === usRegionCode)
        : undefined;
    if (!regionEntry || regionEntry.categories.length === 0) return UNKNOWN_DENY_MESSAGE;
    return {
        title: entry.title,
        description: entry.description.replaceAll('{categories}', spelledOutCategories(regionEntry.categories)),
    };
}

export type GeoComplyAccessDecision =
    | { outcome: 'allowed' }
    | ({ outcome: 'blocked' } & GeoComplyDenyMessage)
    | { outcome: 'verification-required' };

/**
 * The single access decision shared by the boundary UI and the trade
 * request guard, so both always agree. A verified (or verdict-confirmed)
 * region blocks only the categories that region restricts; a confirmed
 * region that doesn't block the trade's categories counts as a pass, like
 * the mobile apps.
 */
export function resolveGeoComplyAccess(
    scope: SportsTradingScope | undefined,
    state: {
        status: GeoComplyStatus;
        validUntil: number | null;
        usRegionCode: string | null;
        denyReason: GeoComplyDenyReason | null;
    },
): GeoComplyAccessDecision {
    if (isGeoComplyVerificationCurrent(state)) {
        if (!isScopeBlockedInRegion(scope, state.usRegionCode)) return { outcome: 'allowed' };
        return { outcome: 'blocked', ...getGeoComplyDenyMessage('not_tradeable_state', state.usRegionCode) };
    }

    if (state.status === 'blocked') {
        const denyReason = state.denyReason ?? 'unknown';
        if (denyReason === 'not_tradeable_state') {
            if (!state.usRegionCode) return { outcome: 'verification-required' };
            if (!isScopeBlockedInRegion(scope, state.usRegionCode)) return { outcome: 'allowed' };
        }
        return { outcome: 'blocked', ...getGeoComplyDenyMessage(denyReason, state.usRegionCode) };
    }

    return { outcome: 'verification-required' };
}

export function assertSportsTradingAllowed(scope: SportsTradingScope | undefined): void {
    // Re-evaluated against the current config: a scope derived before the
    // remote restriction entries loaded must not bake in a stale pass.
    if (scope && !scopeRequiresLocationVerification(scope)) return;
    if (!isGeoComplyUiEnabled()) return;

    const decision = resolveGeoComplyAccess(scope, useGeoComplyStore.getState());
    if (decision.outcome === 'allowed') return;
    if (decision.outcome === 'blocked') {
        throw new SportsTradingLocationError(decision.description, 'blocked');
    }
    throw new SportsTradingLocationError(SPORTS_LOCATION_VERIFICATION_MESSAGE, 'verification-required');
}
